import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import Switch from 'react-switch';

import { TextButton } from '@components/Button/variants';
import { useEventTracking, useNewsletterDetailByIds } from '@hooks';
import useWindowSize from '@hooks/useWindowSize';
import { Theme } from '@interfaces/global';
import { newsletter_list_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { SWITCH_BORDER_CLASSES, SWITCH_COLORS } from '@utils/constants';
import { WIDTH_MD, WIDTH_SM } from '@utils/constants';

import LeadCaptureModalBody, { LeadCaptureModalBodyProps } from '../LeadCaptureModalBody';
import { ModalHeaderProps } from '../common/modalHeader';

export type LeadCaptureModalWithNewslettersProps = ModalHeaderProps & LeadCaptureModalBodyProps;

const LeadCaptureModalWithNewsletters = ({
  buttonText,
  closeIconRef,
  leadType,
  newsletterIds,
  offerLink,
  subtitle,
  title,
}: LeadCaptureModalWithNewslettersProps) => {
  const { width: windowWidth } = useWindowSize();
  const isMobile = windowWidth < WIDTH_SM;
  const { newsletters, isLoadingNewsletters } = useNewsletterDetailByIds({
    newsletter_ids: newsletterIds,
  });
  const { trackEvent } = useEventTracking({});

  const initialValues = useMemo(() => {
    if (!newsletters) return {};
    return newsletters.reduce(
      (newsletterFields, newsletter) => {
        newsletterFields[newsletter.id] = true;
        return newsletterFields;
      },
      {} as Record<string, boolean>
    );
  }, [newsletters]);

  const [values, setValues] = useState<Record<string, boolean>>(initialValues);

  useEffect(() => {
    if (newsletters) {
      setValues(initialValues);
    }
  }, [newsletters, initialValues]);

  useEffect(() => {
    if (closeIconRef?.current) {
      if (windowWidth <= WIDTH_MD) {
        closeIconRef.current.style.color = 'white';
      } else {
        closeIconRef.current.style.color = 'black';
      }
    }
  }, [windowWidth, closeIconRef]);

  if (isLoadingNewsletters || !newsletters) {
    return null;
  }

  const handleToggle = (newsletterId: string, checked: boolean) => {
    setValues((prev) => ({
      ...prev,
      [newsletterId]: checked,
    }));
    const newsletter = newsletters?.find((newsletter) => newsletter.id === parseInt(newsletterId));
    if (newsletter) {
      trackEvent(click({ clickTarget: ClickTarget.NewsletterToggle, clickText: newsletter.name }));
    }
  };

  const colorTheme = Theme.Light;
  const OFF_COLOR =
    SWITCH_COLORS.background.off[colorTheme] ?? SWITCH_COLORS.background.off.default;
  const OFF_HANDLE_COLOR = SWITCH_COLORS.handle.off[colorTheme] ?? SWITCH_COLORS.handle.off.default;
  const ON_COLOR = SWITCH_COLORS.background.on[colorTheme] ?? SWITCH_COLORS.background.on.default;
  const ON_HANDLE_COLOR = SWITCH_COLORS.handle.on[colorTheme] ?? SWITCH_COLORS.handle.on.default;

  const getBorderClasses = (isChecked: boolean) => {
    if (isChecked) {
      return SWITCH_BORDER_CLASSES.on[colorTheme] || '';
    } else {
      return SWITCH_BORDER_CLASSES.off[colorTheme] || '';
    }
  };

  const selectedNewsletterIds = Object.entries(values)
    .filter(([, isSelected]) => isSelected === true)
    .map(([newsletterId]) => Number(newsletterId));

  return (
    <div className="flex w-full flex-wrap">
      <div className="flex w-full items-center justify-center bg-grayscale-charcoal md:max-w-[366px]">
        <div className=" px-32 py-32 font-suisse-works text-headline-md text-white md:px-0 md:py-0 md:pl-28 md:text-headline-lg">
          {title}
        </div>
      </div>
      <div className="w-full flex-1 bg-white px-32 py-32 md:px-64 md:py-48 lg:px-90">
        <div className="mb-16 font-suisse-neue text-subheadline-md md:text-subheadline-lg lg:mb-32">
          {subtitle}
        </div>
        <div className="mb-32 grid grid-cols-1 gap-x-24 gap-y-24 md:mb-48 md:grid-cols-2 lg:gap-x-64">
          {newsletters.map((newsletter, index) => {
            const isChecked = values[newsletter.id] ?? false;
            if (isMobile && index > 1) return null;
            return (
              <div key={newsletter.id} className="flex items-center gap-16">
                <div
                  className={clsx('flex flex-shrink-0 items-center')}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Switch
                    className={clsx('switch', getBorderClasses(isChecked))}
                    width={38}
                    height={24}
                    offColor={OFF_COLOR}
                    offHandleColor={OFF_HANDLE_COLOR}
                    onColor={ON_COLOR}
                    onHandleColor={ON_HANDLE_COLOR}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    checked={isChecked}
                    onChange={(checked) => handleToggle(String(newsletter.id), checked)}
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="font-suisse-intl-mono text-overline-sm text-primary-magenta">
                    {newsletter.frequency}
                    {newsletter.isSubscriberOnly && <span> (Subscriber only)</span>}
                  </div>
                  <div className="font-suisse-works text-headline-xxs sm:text-headline-xs lg:text-headline-sm">
                    {newsletter.name}
                  </div>
                  <div className="font-suisse-screen text-byline-sm text-grayscale-gray">
                    By {newsletter.author}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-col-reverse items-center justify-evenly lg:flex-row">
          <TextButton
            as="link"
            className="mt-24 font-suisse-intl text-button-sm text-grayscale-charcoal lg:mt-0"
            href={newsletter_list_path()}
          >
            See all newsletters
          </TextButton>
          <LeadCaptureModalBody
            buttonText={buttonText}
            leadType={leadType}
            offerLink={offerLink}
            includeSignInOption={false}
            theme={Theme.Light}
            newsletterIds={selectedNewsletterIds}
            withOauth={false}
            emailLeadCaptureClassName="w-full lg:w-1/2"
          />
        </div>
      </div>
    </div>
  );
};

export default LeadCaptureModalWithNewsletters;
